import type { MegaBlockDef, LockedDoorDef, BlockadeWallDef, BarricadeDef } from '../systems/BoardSystem';

/**
 * Pad a grid to target dimensions, centering the source content.
 * Empty cells are filled with -1.
 */
export function padGrid(grid: number[][], targetRows: number, targetCols: number): number[][] {
  const srcRows = grid.length;
  const srcCols = grid[0]?.length ?? 0;
  if (srcRows >= targetRows && srcCols >= targetCols) return grid;

  const padTop = Math.floor((targetRows - srcRows) / 2);
  const padLeft = Math.floor((targetCols - srcCols) / 2);

  const result: number[][] = [];
  for (let r = 0; r < targetRows; r++) {
    const row: number[] = [];
    for (let c = 0; c < targetCols; c++) {
      const srcR = r - padTop;
      const srcC = c - padLeft;
      if (srcR >= 0 && srcR < srcRows && srcC >= 0 && srcC < srcCols) {
        row.push(grid[srcR][srcC]);
      } else {
        row.push(-1); // empty
      }
    }
    result.push(row);
  }
  return result;
}

/**
 * Remap snake column indices after padding.
 */
export function remapSnakeCols(snakeCols: number[], srcCols: number, targetCols: number): number[] {
  if (srcCols >= targetCols) return snakeCols;
  const padLeft = Math.floor((targetCols - srcCols) / 2);
  return snakeCols.map(c => c + padLeft);
}

/**
 * Generic remap for definitions that have row/col fields.
 * Covers MegaBlockDef, LockedDoorDef, and BarricadeDef.
 */
export function remapRowColDefs<T extends { row: number; col: number }>(
  defs: T[],
  sourceGrid: number[][],
  targetRows: number,
  targetCols: number,
): T[] {
  const srcRows = sourceGrid.length;
  const srcCols = sourceGrid[0]?.length ?? 0;
  const padTop = Math.floor((targetRows - srcRows) / 2);
  const padLeft = Math.floor((targetCols - srcCols) / 2);
  return defs.map(def => ({
    ...def,
    row: def.row + padTop,
    col: def.col + padLeft,
  }));
}

/**
 * Remap blockade wall definitions after padding.
 * BlockadeWalls use startCell instead of row/col, and the offset
 * depends on which side the wall is on.
 */
export function remapBlockadeWalls(
  defs: BlockadeWallDef[],
  sourceGrid: number[][],
  targetRows: number,
  targetCols: number,
): BlockadeWallDef[] {
  const srcRows = sourceGrid.length;
  const srcCols = sourceGrid[0]?.length ?? 0;
  const padTop = Math.floor((targetRows - srcRows) / 2);
  const padLeft = Math.floor((targetCols - srcCols) / 2);
  return defs.map(def => ({
    ...def,
    // For left/right walls, startCell is a row index → add padTop
    // For top/bottom walls, startCell is a column index → add padLeft
    startCell: def.startCell + (def.side === 'left' || def.side === 'right' ? padTop : padLeft),
  }));
}

/** Input shape for the pad-and-remap helper (matches ImageLevel optional fields). */
export interface PadRemapInput {
  grid: number[][];
  snakeCols?: number[];
  megaBlocks?: MegaBlockDef[];
  lockedDoors?: LockedDoorDef[];
  blockadeWalls?: BlockadeWallDef[];
  barricades?: BarricadeDef[];
  hammerIndices?: number[];
}

/** Output of padAndRemapLevel with all fields resolved. */
export interface PadRemapResult {
  paddedGrid: number[][];
  snakeCols?: number[];
  megaBlocks?: MegaBlockDef[];
  lockedDoors?: LockedDoorDef[];
  blockadeWalls?: BlockadeWallDef[];
  barricades?: BarricadeDef[];
  hammerIndices?: number[];
}

/**
 * Pad a grid to target dimensions and remap all associated definitions.
 * Eliminates the duplicated pad+remap pattern at call sites.
 */
export function padAndRemapLevel(
  input: PadRemapInput,
  targetRows: number,
  targetCols: number,
): PadRemapResult {
  const paddedGrid = padGrid(input.grid, targetRows, targetCols);
  return {
    paddedGrid,
    snakeCols: input.snakeCols
      ? remapSnakeCols(input.snakeCols, input.grid[0]?.length ?? 0, targetCols)
      : undefined,
    megaBlocks: input.megaBlocks
      ? remapRowColDefs(input.megaBlocks, input.grid, targetRows, targetCols)
      : undefined,
    lockedDoors: input.lockedDoors
      ? remapRowColDefs(input.lockedDoors, input.grid, targetRows, targetCols)
      : undefined,
    blockadeWalls: input.blockadeWalls
      ? remapBlockadeWalls(input.blockadeWalls, input.grid, targetRows, targetCols)
      : undefined,
    barricades: input.barricades
      ? remapRowColDefs(input.barricades, input.grid, targetRows, targetCols)
      : undefined,
    hammerIndices: input.hammerIndices,
  };
}
